import React from 'react';
import { useGame } from '../GameContext';
import { Team } from '../types';
import './Battlefield.css';

interface UnitGroup {
  x: number;
  y: number;
  type: string;
  count: number;
  color: string;
  imageUrl?: string;
}

const Battlefield: React.FC = () => {
  const { gameState } = useGame();

  const raiders = gameState.players.filter(p => p.team === 'raiders');
  const defenders = gameState.players.filter(p => p.team === 'defenders');

  const positionPlayerArmy = (player: typeof raiders[0], rowIndex: number, team: Team): UnitGroup[] => {
    const groups: UnitGroup[] = [];
    const color = team === 'raiders' ? '#d32f2f' : '#1976d2';
    const isLeftSide = team === 'raiders';

    // Row positioning
    const rowHeight = 70;
    const startY = 80;
    const y = startY + rowIndex * rowHeight;

    // Column positioning
    const centerX = 450; // Center of battlefield
    const groupSpacing = 80; // Spacing between unit type groups

    // Count units by type
    const kingCount = player.army.king ? 1 : 0;
    const knightCount = player.army.knights.length;
    const healerCount = player.army.healers.length;
    const archerCount = player.army.archers.length;
    const crossbowCount = player.army.crossbows.length;
    const siegeCount = player.army.siegeEquipment.length;

    // Create array of unit groups with their counts
    const unitTypes: { type: string; count: number; order: number }[] = [];

    if (kingCount > 0) unitTypes.push({ type: 'King', count: kingCount, order: 0 });
    if (knightCount > 0) unitTypes.push({ type: 'Knight', count: knightCount, order: 1 });
    if (healerCount > 0) unitTypes.push({ type: 'Healer', count: healerCount, order: 2 });
    if (archerCount > 0) unitTypes.push({ type: 'Archer', count: archerCount, order: 3 });
    if (crossbowCount > 0) unitTypes.push({ type: 'Crossbow', count: crossbowCount, order: 4 });
    if (siegeCount > 0) unitTypes.push({ type: 'Siege Equipment', count: siegeCount, order: 5 });

    // Position groups based on team side
    const totalGroups = unitTypes.length;
    const totalWidth = (totalGroups - 1) * groupSpacing;

    unitTypes.forEach((unitType, index) => {
      let xPosition: number;

      if (isLeftSide) {
        // Raiders: position to the left of center, reversed order
        // So the first unit type is closest to center (rightmost for raiders)
        const startX = centerX - 100 - totalWidth;
        const reversedIndex = totalGroups - 1 - index;
        xPosition = startX + (reversedIndex * groupSpacing);
      } else {
        // Defenders: position to the right of center
        const startX = centerX + 100;
        xPosition = startX + (index * groupSpacing);
      }

      // For King, use team-specific image URL
      let unitImageUrl = gameState.unitStats[unitType.type as keyof typeof gameState.unitStats]?.imageUrl;
      if (unitType.type === 'King') {
        if (team === 'raiders' && gameState.kingRaidersImageUrl) {
          unitImageUrl = gameState.kingRaidersImageUrl;
        } else if (team === 'defenders' && gameState.kingDefendersImageUrl) {
          unitImageUrl = gameState.kingDefendersImageUrl;
        }
      }

      groups.push({
        x: xPosition,
        y,
        type: unitType.type,
        count: unitType.count,
        color,
        imageUrl: unitImageUrl
      });
    });

    return groups;
  };

  // Position all armies
  const allGroups: UnitGroup[] = [];

  raiders.forEach((player, index) => {
    allGroups.push(...positionPlayerArmy(player, index, 'raiders'));
  });

  defenders.forEach((player, index) => {
    allGroups.push(...positionPlayerArmy(player, index, 'defenders'));
  });

  const renderUnitGroup = (group: UnitGroup, index: number) => {
    const { x, y, type, count, imageUrl, color } = group;
    const size = 65; // Size of the unit image/icon (increased by 30%)
    const isDefender = color === '#1976d2';

    return (
      <g key={`${type}-${index}`}>
        {imageUrl ? (
          // Render image if URL is provided
          <image
            x={x - size / 2}
            y={y - size / 2}
            width={size}
            height={size}
            href={imageUrl}
            preserveAspectRatio="xMidYMid meet"
            className={isDefender ? 'defender-unit' : ''}
            style={isDefender ? { transform: `scaleX(-1)`, transformOrigin: `${x}px ${y}px` } : {}}
          />
        ) : (
          // Fallback to default emoji-based rendering
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="40"
          >
            {getUnitEmoji(type)}
          </text>
        )}

        {/* Count badge */}
        {count > 1 && (
          <>
            <circle
              cx={x + size / 2 - 5}
              cy={y - size / 2 + 5}
              r={12}
              fill="#dc3545"
              stroke="#fff"
              strokeWidth="2"
            />
            <text
              x={x + size / 2 - 5}
              y={y - size / 2 + 5}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="12"
              fontWeight="bold"
              fill="#fff"
            >
              {count}
            </text>
          </>
        )}
      </g>
    );
  };

  const getUnitEmoji = (type: string): string => {
    switch (type) {
      case 'King': return '👑';
      case 'Knight': return '⚔️';
      case 'Archer': return '🏹';
      case 'Crossbow': return '🎯';
      case 'Healer': return '✚';
      case 'Siege Equipment': return '🏛️';
      default: return '❓';
    }
  };

  // Calculate totals
  const getTotalUnits = (players: typeof raiders) => {
    return players.reduce((total, player) => {
      return total +
        (player.army.king ? 1 : 0) +
        player.army.knights.length +
        player.army.healers.length +
        player.army.archers.length +
        player.army.crossbows.length +
        player.army.siegeEquipment.length;
    }, 0);
  };

  const getTeamCounts = (players: typeof raiders) => {
    return players.reduce((counts, player) => {
      return {
        kings: counts.kings + (player.army.king ? 1 : 0),
        knights: counts.knights + player.army.knights.length,
        healers: counts.healers + player.army.healers.length,
        archers: counts.archers + player.army.archers.length,
        crossbows: counts.crossbows + player.army.crossbows.length,
        siege: counts.siege + player.army.siegeEquipment.length
      };
    }, { kings: 0, knights: 0, healers: 0, archers: 0, crossbows: 0, siege: 0 });
  };

  const getTeamStats = (players: typeof raiders): { hp: number; damage: number } => {
    return players.reduce((totals, player) => {
      let hp = 0;
      let damage = 0;

      if (player.army.king) {
        hp += gameState.unitStats['King'].hp;
        damage += gameState.unitStats['King'].damage;
      }

      player.army.knights.forEach(() => {
        hp += gameState.unitStats['Knight'].hp;
        damage += gameState.unitStats['Knight'].damage;
      });

      player.army.archers.forEach(() => {
        hp += gameState.unitStats['Archer'].hp;
        damage += gameState.unitStats['Archer'].damage;
      });

      player.army.crossbows.forEach(() => {
        hp += gameState.unitStats['Crossbow'].hp;
        damage += gameState.unitStats['Crossbow'].damage;
      });

      player.army.healers.forEach(() => {
        hp += gameState.unitStats['Healer'].hp;
        damage += gameState.unitStats['Healer'].damage;
      });

      player.army.siegeEquipment.forEach(() => {
        hp += gameState.unitStats['Siege Equipment'].hp;
        damage += gameState.unitStats['Siege Equipment'].damage;
      });

      return {
        hp: totals.hp + hp,
        damage: totals.damage + damage
      };
    }, { hp: 0, damage: 0 });
  };

  const raidersCounts = getTeamCounts(raiders);
  const defendersCounts = getTeamCounts(defenders);
  const raidersStats = getTeamStats(raiders);
  const defendersStats = getTeamStats(defenders);

  return (
    <div className="battlefield-container">
      <h1 className="battlefield-title">Raid Game Battlefield</h1>

      <div className="teams-info">
        <div className="team-info raiders">
          <h2>Raiders</h2>
          <div className="team-stats">
            <div>Players: {raiders.length}</div>
            <div>Total Units: {getTotalUnits(raiders)}</div>
            <div className="stat-totals">
              <div><strong>Total HP:</strong> {raidersStats.hp}</div>
              <div><strong>Total Damage:</strong> {raidersStats.damage}</div>
            </div>
            <div className="unit-breakdown">
              <div>Kings: {raidersCounts.kings}</div>
              <div>Knights: {raidersCounts.knights}</div>
              <div>Healers: {raidersCounts.healers}</div>
              <div>Archers: {raidersCounts.archers}</div>
              <div>Crossbows: {raidersCounts.crossbows}</div>
              <div>Siege: {raidersCounts.siege}</div>
            </div>
          </div>
        </div>

        <div className="team-info defenders">
          <h2>Defenders</h2>
          <div className="team-stats">
            <div>Players: {defenders.length}</div>
            <div>Total Units: {getTotalUnits(defenders)}</div>
            <div className="stat-totals">
              <div><strong>Total HP:</strong> {defendersStats.hp}</div>
              <div><strong>Total Damage:</strong> {defendersStats.damage}</div>
            </div>
            <div className="unit-breakdown">
              <div>Kings: {defendersCounts.kings}</div>
              <div>Knights: {defendersCounts.knights}</div>
              <div>Healers: {defendersCounts.healers}</div>
              <div>Archers: {defendersCounts.archers}</div>
              <div>Crossbows: {defendersCounts.crossbows}</div>
              <div>Siege: {defendersCounts.siege}</div>
            </div>
          </div>
        </div>
      </div>

      <svg className="battlefield-svg" viewBox="0 0 900 700" preserveAspectRatio="xMidYMid meet">
        {/* Battlefield background */}
        {gameState.battlefieldBackgroundUrl ? (
          <image
            x="0"
            y="0"
            width="900"
            height="700"
            href={gameState.battlefieldBackgroundUrl}
            preserveAspectRatio="xMidYMid slice"
          />
        ) : (
          <rect x="0" y="0" width="900" height="700" fill="#8b7355" />
        )}

        {/* Center dividing line */}
        <line x1="450" y1="0" x2="450" y2="700" stroke="#000" strokeWidth="2" strokeDasharray="10,5" />

        {/* Side labels */}
        <text x="225" y="50" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#d32f2f">
          RAIDERS
        </text>
        <text x="675" y="50" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#1976d2">
          DEFENDERS
        </text>

        {/* Render all unit groups */}
        {allGroups.map((group, index) => renderUnitGroup(group, index))}
      </svg>

      <div className="legend">
        <h3>Legend</h3>
        <div className="legend-items">
          <div className="legend-item">
            <svg width="30" height="30">
              <polygon
                points="3,23 27,23 25,8 20,14 15,3 10,14 5,8"
                fill="#666"
                stroke="#FFD700"
                strokeWidth="1.5"
              />
              <circle cx="15" cy="8" r="1.5" fill="#FFD700" />
              <circle cx="8" cy="11" r="1.5" fill="#FFD700" />
              <circle cx="22" cy="11" r="1.5" fill="#FFD700" />
            </svg>
            <span>King</span>
          </div>
          <div className="legend-item">
            <svg width="30" height="30">
              <circle cx="15" cy="15" r="12" fill="#666" stroke="#000" strokeWidth="2" />
            </svg>
            <span>Knight</span>
          </div>
          <div className="legend-item">
            <svg width="30" height="30">
              <line x1="15" y1="3" x2="15" y2="27" stroke="#666" strokeWidth="3" strokeLinecap="round" />
              <line x1="3" y1="15" x2="27" y2="15" stroke="#666" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <span>Healer</span>
          </div>
          <div className="legend-item">
            <svg width="30" height="30">
              <line x1="15" y1="22" x2="15" y2="8" stroke="#666" strokeWidth="3" strokeLinecap="round" />
              <line x1="15" y1="8" x2="10" y2="13" stroke="#666" strokeWidth="3" strokeLinecap="round" />
              <line x1="15" y1="8" x2="20" y2="13" stroke="#666" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <span>Archer/Crossbow</span>
          </div>
          <div className="legend-item">
            <svg width="30" height="30">
              <rect x="5" y="8" width="20" height="14" fill="#666" stroke="#000" strokeWidth="2" />
            </svg>
            <span>Siege Equipment</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Battlefield;
